import { useQuery } from '@apollo/client/react/index.js';
import {
  AnimatedCounter,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassEmptyState,
  GlassInput,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
  SkeletonLoader,
} from '@merritrac/shared/components';
import { Building, Eye, MapPin, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { GET_PROPERTIES } from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

/* ── GraphQL response types ── */

interface PropertyNode {
  id: number;
  clientCode: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  type: string | null;
  totalUnits: number | null;
  active: number | null;
  client: {
    clientCode: string;
    clientName: string | null;
  };
}

interface PropertyEdge {
  node: PropertyNode;
}

interface PropertyConnection {
  totalCount: number;
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
  edges: PropertyEdge[];
}

interface GetPropertiesData {
  properties: PropertyConnection;
}

/* ── Page ── */

export function PropertiesPage(): ReactElement {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { data, loading, error } = useQuery<GetPropertiesData>(GET_PROPERTIES, {
    variables: { first: 100 },
  });

  const properties: PropertyNode[] = data?.properties.edges.map((e) => e.node) ?? [];

  const filtered = properties.filter((p) => {
    const query = search.toLowerCase();
    if (query === '') {
      return true;
    }
    const name = p.name;
    const clientName = p.client.clientName ?? '';
    const city = p.city ?? '';
    const state = p.state ?? '';
    const type = p.type ?? '';
    return (
      name.toLowerCase().includes(query) ||
      clientName.toLowerCase().includes(query) ||
      city.toLowerCase().includes(query) ||
      state.toLowerCase().includes(query) ||
      type.toLowerCase().includes(query)
    );
  });

  const totalProperties = data?.properties.totalCount ?? properties.length;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">Manage properties and buildings</p>
        <GlassButton
          variant="primary"
          size="md"
          onClick={() => {
            void navigate('/properties/new');
          }}
        >
          <Plus size={16} />
          New Property
        </GlassButton>
      </div>

      {/* Stats */}
      <GlassCard>
        <GlassCardContent className="flex items-center gap-4 py-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 bg-primary-soft">
            <Building size={20} className="text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-semibold text-text leading-none">
              {loading ? (
                <SkeletonLoader className="w-12 h-7" />
              ) : (
                <AnimatedCounter value={totalProperties} />
              )}
            </p>
            <p className="text-xs text-text-secondary mt-1 truncate">Total Properties</p>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Table */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold text-text">All Properties</h2>
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
              />
              <GlassInput
                placeholder="Search properties…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                className="pl-8 w-full sm:w-56"
                aria-label="Search properties"
              />
            </div>
          </div>
        </GlassCardHeader>

        {loading ? (
          <div className="p-5 space-y-3">
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
          </div>
        ) : error !== undefined ? (
          <div className="py-12 text-center text-sm text-danger">
            Failed to load properties: {error.message}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-8">
            <GlassEmptyState
              icon={Building}
              title="No properties found"
              description={
                search !== ''
                  ? 'Try adjusting your search to find what you are looking for.'
                  : 'There are no properties yet. Create one to get started.'
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <GlassTable>
              <GlassTableHead>
                <GlassTableRow>
                  <GlassTableHeaderCell>Property</GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden md:table-cell">
                    Client
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden lg:table-cell">
                    Location
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden sm:table-cell">
                    Units
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell>Status</GlassTableHeaderCell>
                  <GlassTableHeaderCell aria-label="Actions" />
                </GlassTableRow>
              </GlassTableHead>
              <GlassTableBody>
                {filtered.map((property) => {
                  const location = [property.city, property.state].filter(Boolean).join(', ');
                  const isActive = property.active === 1;
                  return (
                    <GlassTableRow
                      key={property.id}
                      className="cursor-pointer"
                      onClick={() => {
                        void navigate(`/properties/${String(property.id)}`);
                      }}
                    >
                      <GlassTableCell>
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium text-text">{property.name}</span>
                          {property.type !== null && (
                            <GlassBadge variant="default" className="w-fit text-xs">
                              {property.type}
                            </GlassBadge>
                          )}
                        </div>
                      </GlassTableCell>
                      <GlassTableCell className="text-text-secondary hidden md:table-cell">
                        {property.client.clientName ?? property.client.clientCode}
                      </GlassTableCell>
                      <GlassTableCell className="hidden lg:table-cell">
                        {location !== '' ? (
                          <span className="flex items-center gap-1 text-text-secondary">
                            <MapPin size={12} className="shrink-0" />
                            {location}
                          </span>
                        ) : (
                          <span className="text-text-tertiary">—</span>
                        )}
                      </GlassTableCell>
                      <GlassTableCell className="text-text-secondary hidden sm:table-cell">
                        {property.totalUnits ?? '—'}
                      </GlassTableCell>
                      <GlassTableCell>
                        <GlassBadge variant={isActive ? 'success' : 'default'}>
                          {isActive ? 'Active' : 'Inactive'}
                        </GlassBadge>
                      </GlassTableCell>
                      <GlassTableCell>
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          aria-label={`View ${property.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            void navigate(`/properties/${String(property.id)}`);
                          }}
                        >
                          <Eye size={14} />
                        </GlassButton>
                      </GlassTableCell>
                    </GlassTableRow>
                  );
                })}
              </GlassTableBody>
            </GlassTable>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
